import { type IEntitiesStore } from 'entities/EntitiesExport'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { RequestAttachment } from 'entities/Attachment/model/RequestAttachment'
import { extractLinksFromText, resolvePreviewAttachments } from 'entities/Attachment'

/**
 * Survey message attachments are persisted only via `media_url`, which contains
 * MMS-eligible media only (`is_allowed_for_media_url === true`). Documents
 * (pdf/xlsx) and mid-size audio are not stored there — they live as short links
 * in the message body. On reload these attachments are reconstructed by
 * resolving those body links through the attachment store, reusing the shared
 * preview-resolution logic in `entities/Attachment`.
 */

/**
 * URLs that appear in the message body but are not represented in `media_url`.
 * These are the candidates for non-media attachments (documents / audio).
 */
const getBodyLinkUrls = (message: string, mediaUrl: RequestAttachment[]): string[] => {
  const knownUrls = new Set(
    mediaUrl
      .flatMap((media) => [media.source_short, media.url, media.source])
      .filter((url): url is string => Boolean(url))
  )

  return [...new Set(extractLinksFromText(message))].filter((url) => !knownUrls.has(url))
}

export const resolveSurveyBodyAttachments = (
  entities: IEntitiesStore,
  message: string,
  mediaUrl: RequestAttachment[]
): Promise<Attachment[]> => {
  const urls = getBodyLinkUrls(message, mediaUrl)
  return resolvePreviewAttachments(entities.attachmentStore, urls)
}

export const restoreSurveyMediaAttachments = (
  mediaUrl: RequestAttachment[] | null | undefined
): Attachment[] =>
  (mediaUrl ?? []).map(
    (requestAttachment) =>
      new Attachment({
        requestAttachment,
        fromMediaUrl: requestAttachment.is_allowed_for_media_url !== false,
      })
  )

/**
 * Safety net for the persistence contract above: a non-media attachment whose
 * short link never made it into the message body would otherwise be lost on
 * save. Such attachments are persisted in `media_url` as a fallback, with an
 * explicit `is_allowed_for_media_url: false` so that restore does not mark
 * them MMS-eligible.
 */
export const collectSurveyAttachmentsMissingFromBody = (
  message: string,
  attachments: Attachment[]
): RequestAttachment[] =>
  attachments
    .filter((attachment) => {
      if (attachment.is_allowed_for_media_url || attachment.media_preview) return false
      const urls = [attachment.source_short, attachment.source].filter((url): url is string =>
        Boolean(url)
      )
      return !urls.some((url) => message.includes(url))
    })
    .map((attachment) => {
      const requestAttachment = new RequestAttachment(attachment)
      requestAttachment.is_allowed_for_media_url = false
      return requestAttachment
    })
